import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { DecisionRequest } from "../api";
import { deferredAgo, isDeferred, nameFromEmail, shortDate, waitingLabel } from "../inbox";

/**
 * One request in the queue: the question, who raised it, how long it has waited.
 *
 * The whole row selects, and selecting changes nothing, so the button carries
 * `data-nonmutating` (AGENTS boundary 11). Age shows on EVERY row because the list
 * is newest first by contract (UX O-1): the oldest wait is the most urgent and the
 * reader finds it by reading, not by an order the server does not offer.
 */
export function RequestRow({
  request,
  selected,
  onSelect,
  now,
}: {
  request: DecisionRequest;
  selected: boolean;
  onSelect: () => void;
  now?: Date;
}) {
  const deferred = isDeferred(request);
  const deferredLine = deferredAgo(request, now);
  const withdrawn = request.status === "withdrawn";
  const decided = request.status === "decided";

  return (
    <li>
      <button
        type="button"
        data-nonmutating
        data-request-row
        aria-current={selected ? "true" : undefined}
        onClick={onSelect}
        className={cn(
          "flex w-full flex-col gap-1 rounded-lg border px-3 py-2.5 text-left outline-none",
          "focus-visible:ring-3 focus-visible:ring-ring/50",
          selected
            ? "border-ks-purple bg-ks-purple-4"
            : "border-ks-grey-4 bg-transparent hover:bg-ks-grey-4",
          withdrawn && "opacity-60",
        )}
      >
        <span className="line-clamp-2 text-sm font-medium text-ks-ink">{request.question_text}</span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ks-grey-1">
          <span>
            Raised by {nameFromEmail(request.raised_by)} ·{" "}
            {request.status === "open"
              ? waitingLabel(request.created_at, now)
              : shortDate(request.created_at)}
          </span>
          {deferred && <Badge variant="secondary">Deferred</Badge>}
          {decided && <Badge variant="outline">Decided</Badge>}
          {withdrawn && <Badge variant="outline">Withdrawn</Badge>}
        </span>
        {deferredLine && <span className="text-xs text-ks-grey-1">{deferredLine}</span>}
      </button>
    </li>
  );
}
